import axios from 'axios';

const api = axios.create({ baseURL: `${process.env.REACT_APP_API_URL || ''}/api` });

// Attach token to every request
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('accessToken');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Auto-refresh on 401
api.interceptors.response.use(
  r => r,
  async err => {
    const orig = err.config;
    if (err.response?.status === 401 && !orig._retry) {
      orig._retry = true;
      const refresh = localStorage.getItem('refreshToken');
      if (refresh) {
        try {
          const { data } = await axios.post(`${process.env.REACT_APP_API_URL || ''}/api/auth/refresh`, { refreshToken: refresh });
          localStorage.setItem('accessToken', data.data.accessToken);
          orig.headers.Authorization = `Bearer ${data.data.accessToken}`;
          return api(orig);
        } catch {
          localStorage.clear();
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(err);
  }
);

// ── Auth ────────────────────────────────────────────────────────────────────
export const authAPI = {
  login:   (body) => api.post('/auth/login', body),
  me:      ()     => api.get('/auth/me'),
  logout:  ()     => { localStorage.clear(); window.location.href = '/login'; },
};

// ── Users ───────────────────────────────────────────────────────────────────
export const usersAPI = {
  list:         (params)      => api.get('/users', { params }),
  get:          (id)          => api.get(`/users/${id}`),
  create:       (body)        => api.post('/auth/register', body),
  update:       (id, body)    => api.patch(`/users/${id}`, body),
  updateStatus: (id, status)  => api.patch(`/users/${id}/status`, { status }),
  updateRoles:  (id, roleIds) => api.put(`/users/${id}/roles`, { roleIds }),
  listRoles:    ()            => api.get('/users/roles/list'),
};

// ── Content ─────────────────────────────────────────────────────────────────
export const contentAPI = {
  categories:     ()           => api.get('/content/categories'),
  getCategory:    (id)         => api.get(`/content/categories/${id}`),
  createCategory: (body)       => api.post('/content/categories', body),
  updateCategory: (id, body)   => api.put(`/content/categories/${id}`, body),
  deleteCategory: (id)         => api.delete(`/content/categories/${id}`),
  files:          (params) => api.get('/content/files', { params }),
  uploadFile:     (form, categorySlug, onUploadProgress) => api.post(
    categorySlug ? `/content/files?category_slug=${encodeURIComponent(categorySlug)}` : '/content/files',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress }
  ),
  createText:     (body)     => api.post('/content/files', body),
  updateFile:     (id, body) => api.put(`/content/files/${id}`, body),
  deleteFile:     (id)       => api.delete(`/content/files/${id}`),
  // Private content (user-to-file assignment)
  listPrivateFiles:    (params)          => api.get('/content/private/files', { params }),
  getPrivateFileUsers: (fileId)          => api.get(`/content/private/files/${fileId}/users`),
  assignPrivateFile:   (fileId, userId)  => api.post('/content/private/assign', { file_id: fileId, user_id: userId }),
  unassignPrivateFile: (fileId, userId)  => api.delete(`/content/private/assign/${fileId}/${userId}`),
};

// ── Announcements ────────────────────────────────────────────────────────────
export const announcementsAPI = {
  list:     (params) => api.get('/announcements', { params }),
  create:   (form)   => api.post('/announcements', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  send:     (id)     => api.post(`/announcements/${id}/send`),
  markRead: (id)     => api.post(`/announcements/${id}/read`),
  audioUrl: (id)     => `${process.env.REACT_APP_API_URL || ''}/api/announcements/${id}/audio`,
};

// ── Analytics ────────────────────────────────────────────────────────────────
export const analyticsAPI = {
  overview:      () => api.get('/analytics/overview'),
  topContent:    (limit = 8) => api.get('/analytics/top-content', { params: { limit } }),
  topPosts:      (limit = 8) => api.get('/analytics/top-posts', { params: { limit } }),
  topBooks:      (limit = 8) => api.get('/analytics/top-books', { params: { limit } }),
  topVideos:     (limit = 8) => api.get('/analytics/top-videos', { params: { limit } }),
  topAudio:      (limit = 8) => api.get('/analytics/top-audio', { params: { limit } }),
  roleBreakdown: () => api.get('/analytics/role-breakdown'),
  dailyViews:    () => api.get('/analytics/daily-views'),
};

// ── Roles ────────────────────────────────────────────────────────────────────
export const rolesAPI = {
  list:   ()           => api.get('/roles'),
  get:    (id)         => api.get(`/roles/${id}`),
  create: (body)       => api.post('/roles', body),
  update: (id, body)   => api.put(`/roles/${id}`, body),
  delete: (id)         => api.delete(`/roles/${id}`),
};

// ── Posts ─────────────────────────────────────────────────────────────────────
export const postsAPI = {
  list:   (params) => api.get('/posts', { params }),
  get:    (id)     => api.get(`/posts/${id}`),
  create: (form)   => api.post('/posts', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, form) => api.put(`/posts/${id}`, form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id)     => api.delete(`/posts/${id}`),
  photoUrl: (id)   => `${process.env.REACT_APP_API_URL || ''}/api/posts/${id}/photo`,
  // categories
  listCategories:   ()           => api.get('/posts/categories'),
  createCategory:   (body)       => api.post('/posts/categories', body),
  updateCategory:   (id, body)   => api.put(`/posts/categories/${id}`, body),
  deleteCategory:   (id)         => api.delete(`/posts/categories/${id}`),
};

// ── Home Tabs ─────────────────────────────────────────────────────────────────
export const homeAPI = {
  getTabs:   ()              => api.get('/home/tabs'),
  updateTab: (tabKey, body)  => api.put(`/home/tabs/${tabKey}`, body),
};

// ── Books ─────────────────────────────────────────────────────────────────────
export const booksAPI = {
  list:     (params) => api.get('/books', { params }),
  get:      (id)     => api.get(`/books/${id}`),
  create:   (form)   => api.post('/books', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update:   (id, form) => api.put(`/books/${id}`, form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete:   (id)     => api.delete(`/books/${id}`),
  coverUrl: (id)     => `${process.env.REACT_APP_API_URL || ''}/api/books/${id}/cover`,
};

// ── Videos ────────────────────────────────────────────────────────────────────
export const videosAPI = {
  list:         (params)       => api.get('/videos', { params }),
  get:          (id)           => api.get(`/videos/${id}`),
  create:       (form, onUploadProgress) => api.post('/videos', form, {
    headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress,
  }),
  update:       (id, form)     => api.put(`/videos/${id}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete:       (id)           => api.delete(`/videos/${id}`),
  streamUrl:    (id)           => `${process.env.REACT_APP_API_URL || ''}/api/videos/${id}/stream`,
  thumbnailUrl: (id)           => `${process.env.REACT_APP_API_URL || ''}/api/videos/${id}/thumbnail`,
  // Comments admin helpers
  listComments: (id, params)   => api.get(`/videos/${id}/comments`, { params }),
  deleteComment:(id, commentId)=> api.delete(`/videos/${id}/comments/${commentId}`),
};

// ── Video Playlists ───────────────────────────────────────────────────────────
export const videoPlaylistsAPI = {
  list:     (params)     => api.get('/video-playlists', { params }),
  get:      (id)         => api.get(`/video-playlists/${id}`),
  create:   (form)       => api.post('/video-playlists', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  update:   (id, form)   => api.put(`/video-playlists/${id}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete:   (id)         => api.delete(`/video-playlists/${id}`),
  coverUrl: (id)         => `${process.env.REACT_APP_API_URL || ''}/api/video-playlists/${id}/cover`,
};

// ── Audio ─────────────────────────────────────────────────────────────────────
export const audioAPI = {
  // Albums
  listAlbums:   (params)       => api.get('/audio/albums', { params }),
  getAlbum:     (id)           => api.get(`/audio/albums/${id}`),
  createAlbum:  (form)         => api.post('/audio/albums', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  updateAlbum:  (id, form)     => api.put(`/audio/albums/${id}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteAlbum:  (id)           => api.delete(`/audio/albums/${id}`),
  albumCoverUrl:(id)           => `${process.env.REACT_APP_API_URL || ''}/api/audio/albums/${id}/cover`,
  // Tracks
  listTracks:   (params)       => api.get('/audio/tracks', { params }),
  getTrack:     (id)           => api.get(`/audio/tracks/${id}`),
  createTrack:  (form, onUploadProgress) => api.post('/audio/tracks', form, {
    headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress,
  }),
  updateTrack:  (id, form)     => api.put(`/audio/tracks/${id}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteTrack:  (id)           => api.delete(`/audio/tracks/${id}`),
  streamUrl:    (id)           => `${process.env.REACT_APP_API_URL || ''}/api/audio/tracks/${id}/stream`,
  thumbnailUrl: (id)           => `${process.env.REACT_APP_API_URL || ''}/api/audio/tracks/${id}/thumbnail`,
  // Comments admin helpers
  listComments: (id, params)   => api.get(`/audio/tracks/${id}/comments`, { params }),
  deleteComment:(id, commentId)=> api.delete(`/audio/tracks/${id}/comments/${commentId}`),
};

// ── Quotes ────────────────────────────────────────────────────────────────────
export const quotesAPI = {
  list:   (params) => api.get('/quotes', { params }),
  create: (body)   => api.post('/quotes', body),
  update: (id, body) => api.put(`/quotes/${id}`, body),
  delete: (id)     => api.delete(`/quotes/${id}`),
};

// ── Feedback ─────────────────────────────────────────────────────────────────
export const feedbackAPI = {
  list:         (params) => api.get('/feedback', { params }),
  updateStatus: (id, status) => api.patch(`/feedback/${id}/status`, { status }),
};

export default api;
